import { useCallback, useState } from 'react';

import { message } from 'antd';
import { generateExcelFromBase64 } from '~/helpers/util/generateExcelFromBase64';
import type { IProps } from './HeaderWithColumnSelectionExport';

export type IStateRequest = {
  isOpen: boolean;
  isLoading: boolean;
  isError: boolean;
  isSuccessful: boolean;
  errorMessage: string;
  isDisabled: boolean;
};

export type IGetExportExcelResponse = {
  data: string;
};

const initialExportState: IStateRequest = {
  isOpen: false,
  isLoading: false,
  isError: false,
  isSuccessful: false,
  errorMessage: '',
  isDisabled: false,
};

export default function useHeaderWithColumnSelectionExport({
  getExcelExport,
  onError,
  fileName,
  onClickOpenModal = () => {},
}: Partial<IProps>) {
  const [exportState, setExportState] = useState<IStateRequest>(initialExportState);
  const [modalVisible, setModalVisible] = useState(false);

  const handleOpenModal = useCallback(async () => {
    try {
      await onClickOpenModal();
      setModalVisible(true);
    } catch (error) {
      console.warn('Erro ao abrir o modal:', error);
    }
  }, [onClickOpenModal]);

  const handleExportClick = useCallback(
    async (selectedColumns: string[]) => {
      setExportState({ ...initialExportState, isOpen: true, isLoading: true });
      setModalVisible(true);

      try {
        const response = await getExcelExport?.(selectedColumns);
        if (!response?.data?.data) {
          throw new Error('Não foi possível obter o arquivo de exportação');
        }

        await generateExcelFromBase64(response.data.data, fileName);

        setExportState({
          ...exportState,
          isLoading: false,
          isSuccessful: true,
        });
      } catch (error) {
        setExportState({
          ...exportState,
          isLoading: false,
          isError: true,
          errorMessage: (error as Error)?.message ?? 'Erro ao exportar arquivo',
        });
        message.error('Erro ao exportar arquivo, tente novamente.');

        if (onError) {
          onError(error as Error);
        }
      }
    },
    [exportState, fileName, getExcelExport, onError],
  );

  const handleModalClose = useCallback(() => {
    setExportState(initialExportState);
    setModalVisible(false);
  }, []);

  return {
    handleOpenModal,
    handleExportClick,
    handleModalClose,
    modalVisible,
    exportState,
  };
}
